import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ApiService } from '../services/api.service';
import { PendingInventoryComponent } from './pending-inventory.component';
import { HumanizeEnumPipe } from '../pipes/humanize-enum.pipe';

interface Invoice {
  id: number;
  invoiceNumber?: string;
  invoiceDate?: Date;
  dueDate?: Date;
  shipDate?: Date;
  customerId?: number;
  customerName?: string;
  customerPO?: string;
  status?: string;
  totalAmount?: number;
  amountPaid?: number;
  balance?: number;
}

@Component({
  standalone: true,
  selector: 'app-salesinvoices',
  templateUrl: './salesinvoices.component.html',
  styleUrl: './salesinvoices.component.css',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    HumanizeEnumPipe
  ]
})
export class SalesInvoicesComponent implements OnInit {
  dataSource = new MatTableDataSource<Invoice>([]);
  displayedColumns: string[] = ['invoiceNumber', 'invoiceDate', 'dueDate', 'shipDate', 'customerName', 'status', 'totalAmount', 'amountPaid'];
  loading: boolean = true;
  error: string | null = null;

  @ViewChild(MatSort) set matSort(s: MatSort) {
    if (s) { this.dataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    if (p) { this.dataSource.paginator = p; }
  }

  constructor(
    private router: Router,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadInvoices();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  loadInvoices(): void {
    this.loading = true;
    this.error = null;
    
    this.apiService.get<any>('invoice/all').subscribe({
      next: (data) => {
        let invoicesArray: any[];
        if (Array.isArray(data)) {
          invoicesArray = data;
        } else if (data && data.invoices) {
          invoicesArray = data.invoices;
        } else if (data && data.data) {
          invoicesArray = data.data;
        } else if (data && data.value) {
          invoicesArray = data.value;
        } else {
          invoicesArray = [];
        }
        
        const invoices = invoicesArray.map(inv => ({
          id: inv.id || inv.Id,
          invoiceNumber: inv.number || inv.Number,
          invoiceDate: inv.date || inv.Date,
          dueDate: inv.dueDate || inv.DueDate,
          shipDate: inv.shipDate || inv.ShipDate || inv.desiredShipDate || inv.DesiredShipDate,
          customerId: inv.customerId || inv.CustomerId,
          customerName: inv.customer?.name || inv.Customer?.Name || inv.customerName || inv.CustomerName || '',
          customerPO: inv.customerPO || inv.CustomerPO,
          status: inv.status || inv.Status,
          totalAmount: inv.totalAmount || inv.TotalAmount,
          amountPaid: inv.amountPaid || inv.AmountPaid,
          balance: (inv.totalAmount || inv.TotalAmount || 0) - (inv.amountPaid || inv.AmountPaid || 0)
        }));
        
        this.dataSource.data = invoices;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading invoices:', err);
        
        if (err.status === 0) {
          this.error = 'Cannot connect to API. Make sure the Web API is running.';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log out and log in again.';
        } else if (err.status === 404) {
          this.error = 'API endpoint not found.';
        } else {
          this.error = `Failed to load invoices: ${err.message || 'Unknown error'}`;
        }
        
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  createNewInvoice(): void {
    this.router.navigate(['/sales-invoices/new']);
  }

  viewInvoice(invoice: Invoice): void {
    this.router.navigate(['/sales-invoices', invoice.id]);
  }

  editInvoice(invoice: Invoice): void {
    this.router.navigate(['/sales-invoices', invoice.id]);
  }
}
